/**
 * 송장 입력 검증·특수 바코드 해석 — 1.0 GL09 `onFilterChange`·`onSearchInvoice` 와 grid.js `toGl09ScanRequest`.
 * 서버를 부르기 전에 막는 것만 여기서 본다. 막히면 요청도 인쇄도 없다(1.0 K7).
 */
import { COMMAND_PREFIX, COMMAND_SUFFIX, INBOUND_REQUESTED_STATUS, MESSAGES, MIN_INVOICE_LENGTH, SCAN_COMMANDS } from './consts'
import type { InboundScanReq, InboundScanRow, ScanCommandType } from './queries/types'

export type ScanInputResult =
  /** request = scan API 요청 · barcode = 라벨 분기에 쓰는 입력 원문(1.0 `newBarcode`) */
  { ok: true; request: InboundScanReq; barcode: string } | { ok: false; message: string }

/** `!@#…$%^` 꼴이면 특수 바코드(명령)다. */
export const isCommandBarcode = (value: string): boolean => value.startsWith(COMMAND_PREFIX) && value.endsWith(COMMAND_SUFFIX)

/** 특수 바코드 → scan 명령. 아는 토큰이 없으면 null(일반 스캔 — 1.0 과 같이 원본송장을 다시 스캔한다). */
export const toScanCommand = (barcode: string): ScanCommandType | null => SCAN_COMMANDS.find(([token]) => barcode.includes(token))?.[1] ?? null

/** 1.0 은 반각 원화(₩)만 막았지만 안내 문구는 전각(￦)이다 — 둘 다 막는다(BE 패턴은 ₩). */
const FORBIDDEN_CHARS = ['\\', '₩', '￦']

/**
 * @param raw 입력 칸 값
 * @param orgInvoice 원본송장(직전에 스캔 API 로 보낸 송장, 1.0 ORG_TRS_INVOICE)
 * @param lastRow 직전 스캔 결과 첫 행(1.0 GL09_HEAD_DATA[0]) — 방금 입고요청된 송장 재스캔 차단용
 */
export const parseScanInput = (raw: string, orgInvoice: string, lastRow?: InboundScanRow): ScanInputResult => {
  const value = raw.trim()
  if (!value) return { ok: false, message: MESSAGES.emptyInvoice }
  if (FORBIDDEN_CHARS.some((c) => value.includes(c))) return { ok: false, message: MESSAGES.forbiddenChar }

  // 특수 바코드는 원본송장에 거는 명령이다 — 원본송장이 없으면 보낼 송장이 없다(1.0 onFilterChange).
  if (isCommandBarcode(value)) {
    if (!orgInvoice) return { ok: false, message: MESSAGES.noOrgInvoice }
    return { ok: true, request: { invoiceNumber: orgInvoice, command: toScanCommand(value) }, barcode: value }
  }

  if (value.length < MIN_INVOICE_LENGTH) return { ok: false, message: MESSAGES.tooShort }
  // 방금 입고요청으로 넘어간 송장을 또 찍으면 서버에 보내지 않는다(1.0 — 직전 결과 첫 행만 본다).
  if (lastRow && lastRow.INB_TRS_INVOICE === value && lastRow.ORD_STATUS === INBOUND_REQUESTED_STATUS) {
    return { ok: false, message: MESSAGES.alreadyInbound }
  }
  return { ok: true, request: { invoiceNumber: value, command: null }, barcode: value }
}
